import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Link,
  IconButton,
  Divider,
  TextField,
  Button,
  Stack,
  Snackbar,
  Alert
} from '@mui/material';
import { motion } from 'framer-motion';
import { useLanguage } from './LanguageProvider';
import { useNavigate, useLocation } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SendIcon from '@mui/icons-material/Send';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import LanguageIcon from '@mui/icons-material/Language';
import logo from '../Images/Almaryah-Legal-Consultancy.png';

export const Footer = () => {
  const { currentLang, direction } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const currentYear = new Date().getFullYear();
  
  // Newsletter state
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const floatingAnimation = {
    y: [0, -5, 0],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  // Navigation function
  const handleNavigation = (path) => {
    if (path.startsWith('/#')) {
      const hash = path.substring(2);
      
      if (location.pathname === '/') {
        const section = document.getElementById(hash);
        if (section) {
          section.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        navigate('/');
        setTimeout(() => {
          const section = document.getElementById(hash);
          if (section) {
            section.scrollIntoView({ behavior: 'smooth' });
          }
        }, 300);
      }
    } else {
      navigate(path);
    }
  };

  // Navigation links - updated with correct branding
  const navLinks = {
    en: [
      { text: 'Home', path: '/' },
      { text: 'About Us', path: '/about' },
      { text: 'Our Services', path: '/services' },
      { text: 'Contact Us', path: '/#contact' }
    ],
    ar: [
      { text: 'الرئيسية', path: '/' },
      { text: 'من نحن', path: '/about' },
      { text: 'خدماتنا', path: '/services' },
      { text: 'اتصل بنا', path: '/#contact' }
    ]
  };

  // Services links - updated with correct terminology
  const servicesLinks = {
    en: [
      { text: 'Legal Consultations', path: '/services' },
      { text: 'Financial Claims ', path: '/services' },
      { text: 'Debt Collection ', path: '/services' },
      { text: 'Legal Notices', path: '/services' },
      { text: 'Administrative Court ', path: '/services' }
    ],
    ar: [
      { text: 'الاستشارات القانونية', path: '/services' },
      { text: 'تسوية المطالبات المالية', path: '/services' },
      { text: 'دعم تحصيل الديون', path: '/services' },
      { text: 'التبليغات والمراسلات الرسمية', path: '/services' },
      { text: 'دعم المحاكم الإداري', path: '/services' }
    ]
  };

  // Updated contact info with real data
  const contactInfo = {
    en: [
      { icon: PhoneIcon, text: '+971 50 333 0841', link: 'tel:+971503330841' },
      { icon: EmailIcon, text: 'info@almaryahlc.com', link: 'mailto:info@almaryahlc.com' },
      { icon: EmailIcon, text: 'almaryahlconsaltancy@gmail.com', link: 'mailto:almaryahlconsaltancy@gmail.com' },
      { icon: LocationOnIcon, text: 'Twin Towers, Fujairah, UAE', link: 'https://maps.google.com/?q=Twin+Towers+Fujairah' },
      { icon: LocationOnIcon, text: 'P.O. Box 4422', link: '#' }
    ],
    ar: [
      { icon: PhoneIcon, text: '+971 50 333 0841', link: 'tel:+971503330841' },
      { icon: EmailIcon, text: 'info@almaryahlc.com', link: 'mailto:info@almaryahlc.com' },
      { icon: EmailIcon, text: 'almaryahlconsaltancy@gmail.com', link: 'mailto:almaryahlconsaltancy@gmail.com' },
      { icon: LocationOnIcon, text: 'برج التوأم، الفجيرة، الإمارات', link: 'https://maps.google.com/?q=Twin+Towers+Fujairah' },
      { icon: LocationOnIcon, text: 'ص.ب ٤٤٢٢', link: '#' }
    ]
  };

  // Updated social media links with correct branding
  const socialMedia = [
    { icon: LinkedInIcon, link: 'https://linkedin.com/company/almaryah-legal-consultancy', color: '#0A66C2', name: 'LinkedIn' },
    { icon: WhatsAppIcon, link: 'https://wa.me/971503330841', color: '#25D366', name: 'WhatsApp' },
    { icon: LanguageIcon, link: 'https://almaryahlc.com', color: '#d4af37', name: 'Website' }
  ];

  // Handle newsletter submit
  const handleNewsletterSubmit = async () => {
    if (!newsletterEmail.trim()) {
      setSnackbar({
        open: true,
        message: currentLang === 'ar' ? 'يرجى إدخال البريد الإلكتروني' : 'Please enter your email',
        severity: 'error'
      });
      return;
    }

    if (!/\S+@\S+\.\S+/.test(newsletterEmail)) {
      setSnackbar({
        open: true,
        message: currentLang === 'ar' ? 'البريد الإلكتروني غير صحيح' : 'Invalid email address',
        severity: 'error'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('https://almariaapi.onrender.com/api/v1/newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: newsletterEmail })
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: currentLang === 'ar' 
            ? '✅ تم الاشتراك في النشرة البريدية بنجاح!' 
            : '✅ Successfully subscribed to newsletter!',
          severity: 'success'
        });
        setNewsletterEmail('');
      } else {
        throw new Error('Subscription failed');
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: currentLang === 'ar' 
          ? '❌ حدث خطأ. حاول مرة أخرى' 
          : '❌ An error occurred. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const currentNavLinks = navLinks[currentLang];
  const currentServicesLinks = servicesLinks[currentLang];
  const currentContactInfo = contactInfo[currentLang];

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <Box
      component="footer"
      sx={{
        background: 'linear-gradient(135deg, #0b0c1e 0%, #1a1b2f 100%)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        pt: { xs: 6, md: 8 },
        pb: 3
      }}
    >
      {/* Animated Background Elements */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 45, 0],
          opacity: [0.03, 0.05, 0.03]
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "linear"
        }}
        style={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(212,175,55,0.1) 0%, rgba(212,175,55,0) 70%)',
          zIndex: 0,
          pointerEvents: 'none'
        }}
      />

      <motion.div
        animate={floatingAnimation}
        style={{
          position: 'absolute',
          bottom: '10%',
          left: '5%',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(212,175,55,0.1) 0%, rgba(212,175,55,0) 70%)',
          zIndex: 0,
          pointerEvents: 'none'
        }}
      />

      {/* Scroll to Top Button */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1, type: "spring", stiffness: 200 }}
        whileHover={{ scale: 1.1, y: -5 }}
        whileTap={{ scale: 0.9 }}
        style={{
          position: 'absolute',
          top: 2,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10
        }}
      >
        <IconButton
          onClick={scrollToTop}
          sx={{
            bgcolor: '#d4af37',
            color: '#0b0c1e',
            width: 50,
            height: 50,
            '&:hover': {
              bgcolor: '#b8942f'
            }
          }}
        >
          <ArrowUpwardIcon />
        </IconButton>
      </motion.div>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <Grid container spacing={4}>
            {/* Logo and Description */}
            <Grid item xs={12} md={4}>
              <motion.div variants={itemVariants}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, cursor: 'pointer' }} onClick={() => handleNavigation('/')}>
                  <motion.div
                    animate={{ 
                      rotate: [0, 10, -10, 0],
                      scale: [1, 1.1, 1]
                    }}
                    transition={{ 
                      duration: 3,
                      repeat: Infinity,
                      repeatType: "reverse",
                      ease: "easeInOut"
                    }}
                  >
                    <Box
                      component="img"
                      src={logo}
                      alt="Almaryah Legal Consultancy"
                      sx={{ width: 70, height: 60, objectFit: 'contain' }}
                    />
                  </motion.div>
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                      fontWeight: 'bold',
                      background: 'linear-gradient(45deg, #d4af37 30%, #b8942f 90%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    {currentLang === 'ar' ? 'الماريه' : 'Almaryah'}
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                    color: 'rgba(255,255,255,0.7)',
                    lineHeight: 1.8,
                    mb: 3
                  }}
                >
                  {currentLang === 'ar'
                    ? 'الماريه للإستشارات القانونيه - شريكك الموثوق في الاستشارات القانونية والخدمات الإدارية في الإمارات.'
                    : 'Almaryah Legal Consultancy - Your trusted partner in legal consultancy and administrative services in the UAE.'}
                </Typography>

                {/* Social Media Icons */}
                <Stack direction="row" spacing={1} sx={{ mb: { xs: 3, md: 0 } }}>
                  {socialMedia.map((social, index) => {
                    const Icon = social.icon;
                    return (
                      <motion.div
                        key={index}
                        whileHover={{ scale: 1.2, y: -5 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <IconButton
                          component="a"
                          href={social.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: social.color,
                            bgcolor: 'rgba(255,255,255,0.1)',
                            width: 36,
                            height: 36,
                            '&:hover': {
                              bgcolor: social.color,
                              color: 'white'
                            }
                          }}
                        >
                          <Icon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </motion.div>
                    );
                  })}
                </Stack>
              </motion.div>
            </Grid>

            {/* Quick Links */}
            <Grid item xs={12} sm={6} md={2}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                    fontWeight: 'bold',
                    color: 'white',
                    mb: 2,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -8,
                      insetInlineStart: 0,
                      width: 40,
                      height: 2,
                      background: 'linear-gradient(90deg, #d4af37, #b8942f)'
                    }
                  }}
                >
                  {currentLang === 'ar' ? 'روابط سريعة' : 'Quick Links'}
                </Typography>

                <Stack spacing={1}>
                  {currentNavLinks.map((link, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ x: direction === 'rtl' ? -5 : 5 }}
                    >
                      <Link
                        component="button"
                        onClick={() => handleNavigation(link.path)}
                        sx={{
                          fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                          color: 'rgba(255,255,255,0.7)',
                          textDecoration: 'none',
                          fontSize: '0.9rem',
                          display: 'inline-block',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          '&:hover': {
                            color: '#d4af37'
                          }
                        }}
                      >
                        {link.text}
                      </Link>
                    </motion.div>
                  ))}
                </Stack>
              </motion.div>
            </Grid>

            {/* Services */}
            <Grid item xs={12} sm={6} md={2}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                    fontWeight: 'bold',
                    color: 'white',
                    mb: 2,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -8,
                      insetInlineStart: 0,
                      width: 40,
                      height: 2,
                      background: 'linear-gradient(90deg, #d4af37, #b8942f)'
                    }
                  }}
                >
                  {currentLang === 'ar' ? 'خدماتنا' : 'Our Services'}
                </Typography>

                <Stack spacing={1}>
                  {currentServicesLinks.map((link, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ x: direction === 'rtl' ? -5 : 5 }}
                    >
                      <Link
                        component="button"
                        onClick={() => handleNavigation(link.path)}
                        sx={{
                          fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                          color: 'rgba(255,255,255,0.7)',
                          textDecoration: 'none',
                          fontSize: '0.9rem',
                          display: 'inline-block',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          '&:hover': {
                            color: '#d4af37'
                          }
                        }}
                      >
                        {link.text}
                      </Link>
                    </motion.div>
                  ))}
                </Stack>
              </motion.div>
            </Grid>

            {/* Contact Info */}
            <Grid item xs={12} md={4}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                    fontWeight: 'bold',
                    color: 'white',
                    mb: 2,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -8,
                      insetInlineStart: 0,
                      width: 40,
                      height: 2,
                      background: 'linear-gradient(90deg, #d4af37, #b8942f)'
                    }
                  }}
                >
                  {currentLang === 'ar' ? 'معلومات الاتصال' : 'Contact Info'}
                </Typography>

                <Stack spacing={2}>
                  {currentContactInfo.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <motion.div
                        key={index}
                        whileHover={{ x: direction === 'rtl' ? -5 : 5 }}
                      >
                        <Link
                          href={item.link}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            color: 'rgba(255,255,255,0.7)',
                            textDecoration: 'none',
                            fontSize: '0.9rem',
                            fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                            '&:hover': {
                              color: '#d4af37'
                            }
                          }}
                        >
                          <Icon sx={{ fontSize: 18, color: '#d4af37' }} />
                          <span>{item.text}</span>
                        </Link>
                      </motion.div>
                    );
                  })}
                </Stack>

                {/* Newsletter Subscription */}
                {/* <Box sx={{ mt: 3 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                      color: 'rgba(255,255,255,0.7)',
                      mb: 1
                    }}
                  >
                    {currentLang === 'ar' ? 'النشرة البريدية' : 'Newsletter'}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      placeholder={currentLang === 'ar' ? 'بريدك الإلكتروني' : 'Your email'}
                      size="small"
                      value={newsletterEmail}
                      onChange={(e) => setNewsletterEmail(e.target.value)}
                      disabled={isSubmitting}
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          '& fieldset': {
                            borderColor: 'rgba(255,255,255,0.2)',
                          },
                          '&:hover fieldset': {
                            borderColor: '#d4af37',
                          },
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: 'rgba(255,255,255,0.5)',
                          opacity: 1
                        }
                      }}
                    />
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button
                        variant="contained"
                        onClick={handleNewsletterSubmit}
                        disabled={isSubmitting}
                        sx={{
                          minWidth: 40,
                          height: 40,
                          background: 'linear-gradient(45deg, #d4af37 30%, #b8942f 90%)',
                          color: '#0b0c1e',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #b8942f 30%, #d4af37 90%)',
                          },
                          '&.Mui-disabled': {
                            background: '#ccc'
                          }
                        }}
                      >
                        <SendIcon sx={{ fontSize: 18 }} />
                      </Button>
                    </motion.div>
                  </Box>
                </Box> */}
              </motion.div>
            </Grid>
          </Grid>

          {/* Divider */}
          <Divider sx={{ 
            my: 4, 
            borderColor: 'rgba(255,255,255,0.1)'
          }} />

          {/* Bottom Bar */}
          <motion.div variants={itemVariants}>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2
            }}>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                  color: 'rgba(255,255,255,0.5)',
                  textAlign: 'center'
                }}
              >
                © {currentYear} {currentLang === 'ar' ? 'الماريه للإستشارات القانونيه. جميع الحقوق محفوظة.' : 'Almaryah Legal Consultancy. All rights reserved.'}
              </Typography>

              {/* <Box sx={{ display: 'flex', gap: 2 }}>
                <Link
                  component="button"
                  onClick={() => handleNavigation('/privacy')}
                  sx={{
                    fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                    color: 'rgba(255,255,255,0.5)',
                    textDecoration: 'none',
                    fontSize: '0.8rem',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    '&:hover': {
                      color: '#d4af37'
                    }
                  }}
                >
                  {currentLang === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}
                </Link>
                <Link
                  component="button"
                  onClick={() => handleNavigation('/terms')}
                  sx={{
                    fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                    color: 'rgba(255,255,255,0.5)',
                    textDecoration: 'none',
                    fontSize: '0.8rem',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    '&:hover': {
                      color: '#d4af37'
                    }
                  }}
                >
                  {currentLang === 'ar' ? 'الشروط والأحكام' : 'Terms & Conditions'}
                </Link>
              </Box> */}
            </Box>
          </motion.div>
        </motion.div>
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
            '& .MuiAlert-icon': {
              alignItems: 'center'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Footer;